import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { clearPublishFilterCache } from '../../server/martinusPublishFilter.js';
import { Person } from '../../users/entities/person.js';
import type { MAVStatus } from '../enums/MAVStatus.js';
import { ContractSupplementApplyHistory } from './AppliedContract.js';
import { ContractSupplement } from './ContractSupplement.js';

export enum ContractMetaInformationCommentType {
  NEW_SUPPLEMENT = 'NEW_SUPPLEMENT',
  SUPPLEMENT_DELETED = 'SUPPLEMENT_DELETED',
  MAV_REJECTION = 'MAV_REJECTION',
  MAV_QUESTION_PRINCIPAL = 'MAV_QUESTION_PRINCIPAL',
  MAV_QUESTION_PSB = 'MAV_QUESTION_PSB',
  SUPPLEMENT_REOPENED = 'SUPPLEMENT_REOPENED',
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  MAV_ACCEPT = 'MAV_ACCEPT',
  CONTRACT_CREATED = 'CONTRACT_CREATED',
  MAV_SKIPPED = 'MAV_SKIPPED',
  PSB_MANUAL = 'PSB_MANUAL',
}

registerEnum({ enum: ContractMetaInformationCommentType, name: 'ContractMetaInformationCommentType' });

@Entity()
export class ContractMetaInformationComment extends AdornisEntity {
  public static override _class = 'ContractMetaInformationComment';

  @Field(type => Date, { default: v => v ?? new Date() })
  public createdAt!: Date;

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id!: string;

  @validate(nonOptional())
  @Field(type => ContractMetaInformationCommentType)
  public type!: ContractMetaInformationCommentType;

  @Field(type => ID)
  public userID: Maybe<string>;

  @Field(type => ID)
  public userRole: Maybe<string>;

  @Field(type => String, {
    resolve: function (this: ContractMetaInformationComment) {
      return async () => {
        if (!this.userID) return 'System';

        const userCol = await getRawCollection<EntityData<Person>>(Person._collectionName);
        const user = await userCol.findOne(
          { _id: this.userID },
          { projection: { _id: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } } },
        );
        if (!user) return 'Kein User gefunden';

        return `${user.profilDaten?.stammdaten?.vorname ?? '---'} ${user.profilDaten?.stammdaten?.nachname ?? '---'}`;
      };
    },
  })
  userFullName: Maybe<string>;

  // describes whether the comment is about an old process which is already finished or aborted
  @Field(type => Boolean, { default: v => v ?? false })
  public oldProcess: Maybe<boolean>;

  @Field(type => ID)
  public linkedSupplementID: Maybe<string>;

  @DataLoadedField(type => ContractSupplement, ({ linkedSupplementID }) => linkedSupplementID)
  public linkedSupplement: Maybe<ContractSupplement>;

  @Field(type => String)
  public text: Maybe<string>;

  @Field(type => ContractSupplementApplyHistory, { default: v => v ?? new ContractSupplementApplyHistory({}) })
  public changeMade: Maybe<ContractSupplementApplyHistory>;
}

@Entity()
export class ContractMetaInformation extends MongoEntity {
  public static override _class = 'ContractMetaInformation';
  public static override _collectionName = 'contracts-meta-info';

  @validate(nonOptional())
  @Field(type => ID)
  public contractID!: string;

  @Field(type => String)
  public mavStatus: Maybe<MAVStatus>;

  @Field(type => [ContractMetaInformationComment], { default: v => v ?? [] })
  public comments: Maybe<ContractMetaInformationComment[]>;

  static override async beforeCreate(entity: ContractMetaInformation): Promise<void> {
    const col = await getRawCollection<EntityData<ContractMetaInformation>>(this._collectionName);
    const infoForContract = await col.findOne({ contractID: entity.contractID });
    if (infoForContract)
      throw new Error(`Contract-Meta-Informationen für diesen Vertrag (${entity.contractID}) existieren bereits.`);
    return super.beforeCreate(entity);
  }

  static override async afterUpdate(
    oldEntity: PartialEntityData<ContractMetaInformation>,
    newEntity: ContractMetaInformation,
  ): Promise<void> {
    // the MAV sees exactly the persons and contracts whose process currently lies with them, which is
    // derived from mavStatus - so the cached publish filters are stale as soon as it flips
    if (newEntity.mavStatus && newEntity.mavStatus !== oldEntity.mavStatus) clearPublishFilterCache();
  }
}
